/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Message, ProcessedChannel } from "@/types";
import { AlertCircle, Copy, Search, Smartphone } from "lucide-react";
import { useState } from "react";

export function JumpToMessageModal({
  isOpen,
  onClose,
  channel,
  message,
  isPotentiallyHidden = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  channel: ProcessedChannel;
  message: Message;
  isPotentiallyHidden?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const isDM = channel.type === "DM";
  const isGroupDM = channel.type === "GROUP_DM";
  const username = isDM && !isGroupDM ? channel.participants?.[0] : null;

  const messageLink = isDM
    ? `https://discord.com/channels/@me/${channel.id}/${message.ID}`
    : channel.metadata.guild
      ? `https://discord.com/channels/${channel.metadata.guild.id}/${channel.id}/${message.ID}`
      : `https://discord.com/channels/${channel.id}/${message.ID}`;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isPotentiallyHidden && (
              <AlertCircle className="h-5 w-5 text-amber-500" />
            )}
            {isPotentiallyHidden
              ? "Finding an Invisible Message"
              : "Jump to Message in Discord"}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {isPotentiallyHidden && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This message contains stickers or system content that can only
                be viewed directly on Discord. It might not even be deletable.
              </AlertDescription>
            </Alert>
          )}

          {isDM && !isGroupDM && (
            <div className="rounded-lg border p-4">
              <h3 className="mb-3 font-medium">Reopen your DM with the User</h3>

              <div className="bg-muted/50 mb-4 rounded-md p-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <p className="text-muted-foreground mb-1 text-xs font-medium">
                      Username
                    </p>
                    <p className="font-mono text-sm">{username}</p>
                  </div>
                  {channel.recipientId && (
                    <div>
                      <p className="text-muted-foreground mb-1 text-xs font-medium">
                        User ID
                      </p>
                      <p className="font-mono text-sm">{channel.recipientId}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Search className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="flex-1">
                    <p className="mb-1 text-sm font-medium">
                      Search friend list
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Look for the username in your friends
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Smartphone className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="flex-1">
                    <p className="mb-1 text-sm font-medium">
                      Use mobile search
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Discord mobile has global user search
                    </p>
                  </div>
                </div>

                {channel.recipientId && (
                  <div className="flex items-start gap-3">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="flex-1">
                      <p className="mb-1 text-sm font-medium">
                        Ban command trick
                      </p>
                      <p className="text-muted-foreground text-sm">
                        Run{" "}
                        <code className="bg-muted rounded px-1 py-0.5 text-xs">
                          /ban {channel.recipientId}
                        </code>{" "}
                        in a test server, then send{" "}
                        <code className="bg-muted rounded px-1 py-0.5 text-xs">
                          &lt;@{channel.recipientId}&gt;
                        </code>{" "}
                        and right-click mention → Message
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Search className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="flex-1">
                    <p className="mb-1 text-sm font-medium">Server search</p>
                    <p className="text-muted-foreground text-sm">
                      Search{" "}
                      <code className="bg-muted rounded px-1 py-0.5 text-xs">
                        from:{username}
                      </code>{" "}
                      in shared servers, then use Quick Switcher
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border p-4">
            <h3 className="mb-3 font-medium">Jump to Message</h3>

            {isDM ? (
              isGroupDM ? (
                <p className="text-muted-foreground mb-3 text-sm">
                  Note: This won't work if you've left the group DM.
                </p>
              ) : (
                <p className="text-muted-foreground mb-3 text-sm">
                  Note: This only works if you have an open DM conversation with
                  the user.
                </p>
              )
            ) : (
              <p className="text-muted-foreground mb-3 text-sm">
                Note: You must have access to{" "}
                <span className="bg-muted rounded-full px-2 py-0.5 text-xs font-medium">
                  {channel.serverName}
                </span>{" "}
                and permission to view{" "}
                <span className="bg-muted rounded-full px-2 py-0.5 text-xs font-medium">
                  #{channel.displayName}
                </span>
                .
              </p>
            )}

            <div className="space-y-3">
              <div>
                <label className="text-muted-foreground mb-2 block text-xs font-medium">
                  Message Link
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    readOnly
                    value={messageLink}
                    className="font-mono text-sm"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(messageLink)}
                    className="shrink-0"
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="text-muted-foreground font-mono text-sm">
                  1.
                </div>
                <p className="text-sm">Copy the message link above</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground font-mono text-sm">
                  2.
                </div>
                <p className="text-sm">
                  Press{" "}
                  <kbd className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs">
                    Ctrl/Cmd + K
                  </kbd>{" "}
                  to open Quick Switcher
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground font-mono text-sm">
                  3.
                </div>
                <p className="text-sm">Paste the link and press Enter</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
